import React, { useEffect, useState } from "react";
import { Card, CardHeader, Grid, IconButton, List, Theme } from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import slash from "slash";
import {
  ArrowTooltip,
  CheckboxWithLabel,
  DenseDivider,
  DraggableDialog,
  IconToolBar,
  InputWithLabel,
  PromptDialog,
  SimpleDialog,
  SlimScrollbar,
  ToolBarButton
} from "../components";
import { dialog, fs } from "../electron-modules";
import { customURLFilter } from "../file-extensions";
import { useDialog, useUniqueProjectID } from "../hooks";
import {
  createCustomLink,
  updateCustomLink,
  CustomURL,
  selectAllCustomLinks,
  deleteCustomLink
} from "../redux/custom-links-slice";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GetAppIcon from '@mui/icons-material/GetApp';
import { toast } from "../app";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { toLocaleString } from "../utilities";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      margin: theme.spacing(2),
      marginTop: theme.spacing(0),
      padding: theme.spacing(1),
      userSelect: "none"
    },
    cardHeader: {
      padding: theme.spacing(1)
    },
    cardButtons: {
      marginTop: 0,
      alignSelf: "center"
    },
    headerTitle: {
      fontWeight: "bold",
      cursor: "pointer"
    },
    cardContent: {
      margin: theme.spacing(1,2),
      padding: theme.spacing(0),
      paddingBottom: "0px !important",
      wordBreak: "break-all"
    }
  })
);

interface EditLinkDialogProps {
  open: boolean;
  onClose: () => void;
  values: CustomURL;
}

const EditLinkDialog = (props: EditLinkDialogProps) => {
  const {open, onClose, values} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [target, setTarget] = useState("");
  const [showInScene, setShowInScene] = useState(false);
  const [showInAerial, setShowInAerial] = useState(false);

  const addNewLink = !values;

  const handleSubmit = () => {
    onClose();

    if (addNewLink) {
      dispatch(createCustomLink({
        name,
        url,
        target,
        showInScene,
        showInAerial
      }));
    } else {
      dispatch(updateCustomLink({
        linkID: values.id,
        name,
        url,
        target,
        showInScene,
        showInAerial
      }));

      toast.success(t('toast.updated_custom_url'));
    }
  };

  const resetDefaults = () => {
    setName("");
    setUrl("");
    setTarget("");
    setShowInScene(false);
    setShowInAerial(false);
  };

  const loadCustomValues = () => {
    setName(values.name);
    setUrl(values.url);
    setTarget(values.target);
    setShowInScene(values.showInScene);
    setShowInAerial(values.showInAerial);
  };

  useEffect(() => {
    if (!open) return;

    if (addNewLink) {
      resetDefaults();
    } else {
      loadCustomValues();
    }
  }, [open]);

  const canSubmit = (name !== "")
    && (url !== "")
    && (target !== "");

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      maxWidth={"xs"}
      title={addNewLink
        ? t('custom-link.new_custom_link')
        : t('custom-link.edit_custom_link')
      }
      button={t('buttons.submit')}
    >
      <Grid container spacing={2}>

        <Grid item xs={12}>
          <InputWithLabel
            title={t('custom-link.link_name')}
            label={t('custom-link.the_name_that_will_be_displayed')}
            value={name}
            onChange={setName}
          />
        </Grid>

        <Grid item xs={12}>
          <InputWithLabel
            multiline
            title={t('custom-link.link_url')}
            label={<React.Fragment>
              <span>{t('custom-link.the_url_supports_the_following_special_values')}</span>
              <ul style={{marginBottom: "0px"}}>
                <li>{t('custom-link.the_current_x_coordinate')}</li>
                <li>{t('custom-link.the_current_y_coordinate')}</li>
                <li>{t('custom-link.the_current_z_coordinate')}</li>
              </ul>
            </React.Fragment>}
            value={url}
            onChange={setUrl}
          />
        </Grid>

        <Grid item xs={12}>
          <InputWithLabel
            title={t('custom-link.target')}
            label={t('custom-link.set_to_blank_to_open_links_in_a_new_tab')}
            value={target}
            onChange={setTarget}
          />
        </Grid>

        {/** Display in scene view */}
        <Grid item xs={12}>
          <CheckboxWithLabel
            title={t('custom-link.display_in_scene_view')}
            label={t('custom-link.custom_context_menu_link_scene')}
            value={showInScene}
            onChange={setShowInScene}
          />
        </Grid>

        {/** Display in aerial view */}
        <Grid item xs={12}>
          <CheckboxWithLabel
            title={t('custom-link.display_in_aerial_view')}
            label={t('custom-link.custom_context_menu_link_aerial')}
            value={showInAerial}
            onChange={setShowInAerial}
          />
        </Grid>

      </Grid>
    </SimpleDialog>
  );
};

interface CustomLinkProps {
  values: CustomURL;
}

const CustomLink = (props: CustomLinkProps) => {
  const {values} = props;

  const classes = useStyles();
  const {i18n, t} = useTranslation();
  const dispatch = useDispatch();
  const editDialog = useDialog();
  const deleteDialog = useDialog();

  const dateString = toLocaleString(values.date, i18n.language);

  const handleEdit = () => {
    editDialog.handleOpen(values);
  };

  const handleDownload = async () => {
    const result = await dialog.showSaveDialog( {
      defaultPath: `${values.name}.json`,
      filters: customURLFilter,
    });

    if (result.canceled) return;

    const jsonData = {
      name: values.name,
      url: values.url,
      target: values.target,
      showInScene: values.showInScene,
      showInAerial: values.showInAerial
    };

    const text = JSON.stringify(jsonData, null, 2);
    const savePath = slash(result.filePath);
    fs.writeFileSync(savePath, text);
  };

  const handleDeleteSubmit = () => {
    dispatch(deleteCustomLink(values.id));
  };

  return (
    <React.Fragment>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          classes={{
            action: classes.cardButtons
          }}
          action={
            <React.Fragment>
              {/** Download definition */}
              <ArrowTooltip title={t('custom-link.download_definition')}>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                >
                  <GetAppIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

              {/** Edit values */}
              <ArrowTooltip title={t('custom-link.edit')}>
                <IconButton
                  size="small"
                  onClick={handleEdit}
                >
                  <EditIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

              {/** Delete */}
              <ArrowTooltip title={t('custom-link.delete')}>
                <IconButton
                  size="small"
                  onClick={deleteDialog.handleOpen}
                >
                  <DeleteIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

            </React.Fragment>
          }
          title={values.name}
          titleTypographyProps={{
            variant: "body2",
            className: classes.headerTitle
          }}
          subheader={dateString}
          subheaderTypographyProps={{
            variant: "caption"
          }}
        />
        <CardContent className={classes.cardContent}>
          <div>
            <Typography variant="caption">
              <strong>{t('custom-link.url')}:&nbsp;</strong>
            </Typography>
            <Typography variant="caption">
              {values.url}
            </Typography>
          </div>

          <div>
            <Typography variant="caption">
              <strong>{t('custom-link.target')}:&nbsp;</strong>
            </Typography>
            <Typography variant="caption">
              {values.target}
            </Typography>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={handleDeleteSubmit}
        title={t('custom-link.delete_custom_url')}
        prompt={t('custom-link.do_you_want_to_delete_this_custom_url')}
        button={t("buttons.delete")}
      />

      {/* Edit Dialog */}
      <EditLinkDialog
        open={editDialog.open}
        onClose={editDialog.handleClose}
        values={editDialog.data}
      />
    </React.Fragment>
  );
};

interface CustomContextURLProps {
  open: boolean;
  onClose: () => void;
}

export const CustomContextURL = (props: CustomContextURLProps) => {
  const {open, onClose} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();
  const newURLDialog = useDialog();
  const uniqueProjectID = useUniqueProjectID();
  const customLinks = useSelector(selectAllCustomLinks);

  const handleNewLink = () => {
    newURLDialog.handleOpen();
  };

  const handleImport = async () => {
    const result = await dialog.showOpenDialog({
      properties: ['openFile'],
      filters: customURLFilter
    });

    if (result.canceled) return;

    let filePath = slash(result.filePaths[0]);

    try {
      const text = fs.readFileSync(filePath, 'utf8');
      const data = JSON.parse(text);
      importURLData(data);
    } catch {
      toast.error(t('toast.error_reading_custom_url_definition'));
    }
  };

  const importURLData = (values) => {
    const valid = values.hasOwnProperty("name")
      && values.hasOwnProperty("url")
      && values.hasOwnProperty("target")
      && values.hasOwnProperty("showInScene")
      && values.hasOwnProperty("showInAerial");

    if (valid) {
      dispatch(createCustomLink(values));
      return;
    }

    throw new Error("Invalid definition");
  };

  useEffect(() => {
    /** Close dialog when new project is loaded */
    onClose();
  }, [uniqueProjectID]);

  return (
    <React.Fragment>
      <DraggableDialog
        initialHeight={500}
        title={t('custom-link.custom_links')}
        open={open}
        onClose={onClose}
      >
        <IconToolBar>

          {/* Add new operation */}
          <ToolBarButton
            success
            title={t('custom-link.add_new_url')}
            icon={"fa-plus-circle"}
            onClick={handleNewLink}
          />

          {/* Import definition file */}
          <ToolBarButton
            title={t('custom-link.import_custom_url')}
            icon={"fa-upload"}
            onClick={handleImport}
          />

        </IconToolBar>

        <DenseDivider variant="middle" spacing={5}/>

        <SlimScrollbar>
          <List>
            {customLinks.map(data =>
              <CustomLink key={data.id} values={data}/>
            )}
          </List>
        </SlimScrollbar>

      </DraggableDialog>

      <EditLinkDialog
        open={newURLDialog.open}
        onClose={newURLDialog.handleClose}
        values={newURLDialog.data}
      />
    </React.Fragment>
  );
};
